import { Box, Button, Grid, Text, toast, useConfirmation } from '@approvalmax/ui/src/components';
import { useAtomValue, useSetAtom } from 'jotai';
import { memo, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useCreatePerson, useInvalidateGetKyc, useSendInvitationEmail } from 'shared/data/walletsKyc/v1';
import { useCompanyIdBasedOnUrl } from 'shared/hooks/useCompanyIdBasedOnUrl';

import { addPersonPopupNodeState, addPersonPopupOpenState } from '../../../AddPersonPopup/AddPersonPopup.states';
import { useFormSubmit } from '../../AddPersonWizard.hooks';
import type { FormValues } from '../../AddPersonWizard.types';
import { messages } from './SendInvitation.messages';
import { SendInvitationProps } from './SendInvitation.types';

export const SendInvitation = memo<SendInvitationProps>((props) => {
    const { step } = props;

    const { companyId } = useCompanyIdBasedOnUrl();
    const setOpenAddPersonPopup = useSetAtom(addPersonPopupOpenState);
    const addPersonPopupNode = useAtomValue(addPersonPopupNodeState);
    const form = useFormContext<FormValues>();
    const email = form.getValues('person.email');
    const { confirmation } = useConfirmation({
        root: addPersonPopupNode,
        confirmTitle: messages.confirmTitle,
        confirmMessage: messages.confirmMessage({ email: <b>{email}</b> }),
        cancelButtonMessage: messages.confirmCancel,
        confirmButtonMessage: messages.confirm,
    });
    const invalidateGetKyc = useInvalidateGetKyc();
    const { mutate: sendInvitationEmail, isLoading: isLoadingSendInvitationEmail } = useSendInvitationEmail({
        onSuccess: () => {
            setOpenAddPersonPopup(false);
            toast.success(messages.invitationSent);
        },
    });
    const { mutate: createPerson, isLoading: isLoadingCreatePerson } = useCreatePerson({
        onSuccess: ({ personId }) => {
            sendInvitationEmail({
                params: { path: { organizationId: companyId, personId } },
                body: { initialStep: step },
            });
            void invalidateGetKyc({ path: { organizationId: companyId } });
        },
    });
    const { onSubmit } = useFormSubmit({ mutate: createPerson });

    const handleClick = useCallback(() => {
        void confirmation()
            .then(() => onSubmit(form.getValues(), step))
            .catch(() => {});
    }, [confirmation, form, onSubmit, step]);

    return (
        <Box color='midnight20' spacing='24' radius='medium'>
            <Grid gridTemplateColumns='auto max-content' alignItems='center' gap={16}>
                <Text font='body' fontSize='medium' fontWeight='regular'>
                    {messages.message({ email: <b>{email}</b> })}
                </Text>

                <Button
                    size='small'
                    color='blue80'
                    progress={isLoadingCreatePerson || isLoadingSendInvitationEmail}
                    onClick={handleClick}
                    variant='outline'
                >
                    {messages.sendInvitation}
                </Button>
            </Grid>
        </Box>
    );
});

SendInvitation.displayName = 'SendInvitation';
